import * as React from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import Spinner from 'components/spinner/Spinner';
import withInfluencerMeta from 'components/brands-filter/WithInfluencerMeta';

import InfluencerModalActionsMenuItems from './items/InfluencerModalActionsMenuItems';

import { fetchRecents, resetRecents } from 'modules/recents/reducers/recents';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';

import { __t, applyTranslations } from 'utils/language';
import { enrichWithCurrentCollection } from './utils/influencer-modal-actions';

import './influencer-modal-actions-menu.scss';

import type {
  InfluencerMeta,
  InfluencerTile, List,
  RecentsActionType,
  RecentsMetaKey,
  RecentsResourceType,
  RecentsResultEnhanced
} from 'types';

applyTranslations(require('./influencer-modal-actions-menu.i18n.json'));

type OwnProps = {
  resourceType: RecentsResourceType,
  actionType: RecentsActionType,
  metaKey: RecentsMetaKey,
  orgId: number,
  influencerProfile: InfluencerTile,
  influencerMeta?: InfluencerMeta,
  activeItemId?: string | number | null,
  onItemClick: (b: RecentsResultEnhanced | undefined | null, a: boolean) => Promise<void>,
  openModalTrigger: React.ReactNode, // pass an element which will trigger modal with full list,
  createNewTrigger: React.ReactNode // pass an element which will create new empty campaign/collection
};

type StateProps = {
  results: Array<RecentsResultEnhanced>,
  isLoading: boolean,
  list: List
};

type DispatchProps = {
  dispatchFetchRecents: (d: RecentsResourceType, c: number, b: number, a: RecentsActionType) => void,
  dispatchResetRecents: () => void,
  dispatchFetchInfluencersMeta: (a: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const InfluencerModalActionsMenu = (
  {
    resourceType,
    actionType,
    metaKey,
    orgId,
    list,
    results,
    isLoading,
    influencerMeta,
    influencerProfile,
    activeItemId,
    onItemClick,
    openModalTrigger,
    createNewTrigger,
    dispatchFetchRecents,
    dispatchResetRecents,
    dispatchFetchInfluencersMeta
  }: Props
): JSX.Element => {
  const influencerProfileId = influencerProfile.id;
  const fetchRecentEntries = () =>
    dispatchFetchRecents(resourceType, orgId, influencerProfileId, actionType);

  React.useEffect(() => {
    fetchRecentEntries();
    dispatchFetchInfluencersMeta(influencerProfileId);

    return () => {
      dispatchResetRecents();
    };
  }, [
    influencerProfileId,
    dispatchFetchRecents,
    dispatchResetRecents,
    dispatchFetchInfluencersMeta
  ]);

  // since if inside collection view we have to always show current collection in th list
  // add current collection if it's not in the recents list
  const enrichedResults = (resourceType === 'creator-lists' && list?.type === 'custom')
    ? enrichWithCurrentCollection(results, list, influencerProfile.id)
    : results;
  const isNotEmpty = !isEmpty(enrichedResults);
  const hasCampaignIds = resourceType === 'campaigns' && Boolean(influencerMeta?.[metaKey].brand.length);

  return (
    <div className="influencer-modal-actions-menu">
      {
        isLoading ?
          <Spinner
            position="inline"
            size="small"
            color="light"
          /> :
          <React.Fragment>
            <div className="influencer-modal-actions-menu__title">
              {__t('influencer_modal_actions_menu.title')}
            </div>
            <InfluencerModalActionsMenuItems
              metaKey={metaKey}
              activeItemId={activeItemId}
              resourceType={resourceType}
              influencerMeta={influencerMeta}
              items={enrichedResults}
              onItemClick={(item: RecentsResultEnhanced | undefined | null, isSelected: boolean) => {
                onItemClick(item, isSelected).then(fetchRecentEntries);
              }}
            />
            <div className="influencer-modal-actions-menu__action">
              {
                (isNotEmpty || hasCampaignIds)
                  ? openModalTrigger
                  : createNewTrigger
              }
            </div>
          </React.Fragment>
      }
    </div>
  );
};

const mapStateToProps = ({ recents, lists }): StateProps => ({
  results: recents.results,
  isLoading: recents.isLoading,
  list: lists.currentList.list
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchRecents: (
    resourceType: RecentsResourceType,
    orgId: number,
    creatorId: number,
    actionType: RecentsActionType
  ) => (
    dispatch(fetchRecents(resourceType, orgId, creatorId, actionType))
  ),

  dispatchResetRecents: () => (
    dispatch(resetRecents())
  ),

  dispatchFetchInfluencersMeta: (influencerId: number) => (
    dispatch(fetchInfluencersMeta([influencerId]))
  )
});

const EnhancedInfluencerModalActionsMenu = withInfluencerMeta()(InfluencerModalActionsMenu);

export default connect(mapStateToProps, mapDispatchToProps)(EnhancedInfluencerModalActionsMenu);
